<template>

    <Head :title="`Estado de cuenta`" />
    <div class="min-h-screen bg-gradient-to-br from-teal-100 to-white flex flex-col items-center py-8">
        <div class="w-full max-w-2xl bg-white rounded-2xl shadow-lg border border-teal-100 p-6">
            <!-- Encabezado -->
            <div class="flex flex-col items-center mb-6">
                <i class="fa fa-user-circle text-4xl text-teal-600 mb-2"></i>
                <h1 class="text-2xl font-bold text-teal-800 text-center">Estado de Cuenta</h1>
                <p class="text-gray-500 text-center">Consulta tu estado de cuenta en <strong>{{ client?.account?.name ||
                        'Nuestra plataforma' }}</strong>.</p>
            </div>

            <!-- Información del Cliente -->
            <div class="mb-6">
                <div class="flex items-center gap-2 mb-1">
                    <i class="fa fa-user-circle text-teal-500"></i>
                    <span class="font-semibold">Cliente:</span>
                    <span>{{ client.name }}</span>
                </div>
                <div class="flex items-center gap-2 mb-1">
                    <i class="fa fa-address-card text-gray-400"></i>
                    <span>{{ client.identification }}</span>
                </div>
                <div class="flex items-center gap-2 mb-1">
                    <i class="fa fa-envelope text-gray-400"></i>
                    <span>{{ client.email || '-' }}</span>
                </div>
                <div class="flex items-center gap-2 mb-1">
                    <i class="fa fa-phone text-gray-400"></i>
                    <span>{{ client.phone || '-' }}</span>
                </div>
            </div>

            <!-- Resumen de Cuenta -->
            <div class="mb-6 bg-gray-50 rounded-lg p-4">
                <h2 class="font-semibold text-lg mb-3 flex items-center gap-2">
                    <i class="fa fa-wallet text-teal-500"></i> Resumen de Cuenta
                </h2>
                <div class="grid grid-cols-2 gap-4">
                    <div class="bg-white p-3 rounded-lg shadow-sm">
                        <div class="text-sm text-gray-500">Saldo Actual</div>
                        <div class="text-xl font-bold" :class="client.balance > 0 ? 'text-red-600' : 'text-green-600'">
                            ${{ Math.abs(client.balance).toFixed(2) }}
                            <span class="text-sm font-normal">{{ client.balance > 0 ? 'Debe' : 'A favor' }}</span>
                        </div>
                    </div>
                    <div class="bg-white p-3 rounded-lg shadow-sm">
                        <div class="text-sm text-gray-500">Total Compras</div>
                        <div class="text-xl font-bold text-teal-700">${{ totalPurchases.toFixed(2) }}</div>
                    </div>
                </div>
            </div>

            <!-- Historial de Compras -->
            <div class="mb-6">
                <h2 class="font-semibold text-lg mb-2 flex items-center gap-2">
                    <i class="fa fa-history text-teal-500"></i> Historial de Compras
                </h2>
                <div v-if="orders.length" class="space-y-3">
                    <div v-for="order in orders" :key="order.id"
                        class="bg-white border rounded-lg p-3 hover:shadow-md transition-shadow">
                        <div class="flex justify-between items-start mb-2">
                            <div>
                                <div class="font-semibold">Orden #{{ order.id }}</div>
                                <div class="text-sm text-gray-500">{{ formatDate(order.created_at) }}</div>
                            </div>
                            <div class="text-right">
                                <div class="font-semibold">${{ order.total.toFixed(2) }}</div>
                                <div class="text-sm" :class="{
                                    'text-green-600': order.order_state === 'finish',
                                    'text-yellow-600': order.order_state === 'request',
                                    'text-red-600': order.order_state === 'cancel'
                                }">
                                    {{ getOrderState(order.order_state) }}
                                </div>
                            </div>
                        </div>
                        <div class="text-sm text-gray-600">

                        </div>
                        <a :href="`/public/orders/${order.id}`"
                            class="text-teal-600 text-sm hover:text-teal-800 mt-2 inline-block">
                            Ver detalles <i class="fa fa-arrow-right ml-1"></i>
                        </a>
                    </div>
                </div>
                <div v-else class="text-gray-400 py-4 text-center">
                    No hay compras registradas
                </div>
            </div>

            <!-- Historial de Pagos -->
            <div class="mb-6">
                <h2 class="font-semibold text-lg mb-2 flex items-center gap-2">
                    <i class="fa fa-credit-card text-teal-500"></i> Historial de Pagos
                </h2>
                <div v-if="payments.length" class="space-y-3">
                    <div v-for="payment in payments" :key="payment.id"
                        class="bg-white border rounded-lg p-3 hover:shadow-md transition-shadow">
                        <div class="flex justify-between items-start">
                            <div>
                                <div class="font-semibold">{{ payment.method }}</div>
                                <div class="text-sm text-gray-500">{{ formatDate(payment.created_at) }}</div>
                            </div>
                            <div class="text-right">
                                <div class="font-semibold">${{ payment.amount.toFixed(2) }}</div>
                                <div class="text-sm" :class="{
                                    'text-green-600': payment.status === 'approve',
                                    'text-yellow-600': payment.status === 'pending',
                                    'text-red-600': payment.status === 'reject'
                                }">
                                    {{ getPaymentStatus(payment.status) }}
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
                <div v-else class="text-gray-400 py-4 text-center">
                    No hay pagos registrados
                </div>
            </div>

            <div class="text-center text-gray-400 text-xs mt-8">
                Esta página es solo para consulta. Si tienes dudas, contacta a tu vendedor.
            </div>
            <div class="flex flex-col items-center justify-center mt-4">
                <span class="text-sm text-gray-400 font-semibold mb-1 flex items-center gap-2">
                    Powered by
                    <a target="_blank" href="https://realgestion.lat">
                        <img src="/logo.png" alt="Logo" class="h-12 w-auto opacity-90 inline-block align-middle"
                            style="display:inline-block;vertical-align:middle;" />
                    </a>
                </span>
            </div>
        </div>
    </div>
</template>

<script>
import { Head } from '@inertiajs/inertia-vue3'
export default {
    components: {
        Head
    },
    props: {
        client: {
            type: Object,
            required: true
        },
        orders: {
            type: Array,
            default: () => []
        },
        payments: {
            type: Array,
            default: () => []
        }
    },
    computed: {
        totalPurchases() {
            return this.orders.reduce((total, order) => total + order.total, 0);
        }
    },
    methods: {
        formatDate(date) {
            if (!date) return '-';
            return new Date(date).toLocaleDateString('es-VE', {
                year: 'numeric',
                month: 'short',
                day: 'numeric'
            });
        },
        getOrderState(state) {
            const states = {
                'finish': 'Completada',
                'request': 'Pendiente',
                'cancel': 'Cancelada'
            };
            return states[state] || state;
        },
        getPaymentStatus(status) {
            const statuses = {
                'approve': 'Aprobado',
                'pending': 'Pendiente',
                'reject': 'Rechazado'
            };
            return statuses[status] || status;
        }
    }
}
</script>

<style scoped>
.btn-blue {
    @apply bg-teal-700 hover:bg-teal-800 text-white rounded-lg shadow transition-colors;
}
</style>